import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { PageTitle } from '@/components/layout/PageTitle';
import { PageToolbar } from '@/components/layout/PageToolbar';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { SearchInput } from '@/components/common/SearchInput';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { ExportButton } from '@/components/common/ExportButton';
import { Button } from '@/components/ui/button';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useJobCardPage, type JobCardRoute } from '../hooks/useJobCardPage';
import type { JobCardDownloadFn, JobCardQueryOptionsFn } from '../api/attendance.api';

interface JobCardPageProps {
  queryOptions: JobCardQueryOptionsFn;
  onExport: JobCardDownloadFn;
  title: string;
  employeeReportRoute: string;
  selfRoute: JobCardRoute;
}

export function JobCardPage({
  queryOptions,
  onExport,
  title,
  employeeReportRoute,
  selfRoute,
}: JobCardPageProps) {
  const {
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    today,
    isActive,
    setIsActive,
    searchText,
    setSearchText,
    filtered,
    summary,
    columns,
    isLoading,
    error,
    refetch,
  } = useJobCardPage({ queryOptions, employeeReportRoute, selfRoute });

  useDocumentTitle(title);

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageTitle>{title}</PageTitle>
      <PageToolbar currentTitle={title} className="order-1 lg:order-0">
        <SearchInput
          value={searchText}
          onChange={setSearchText}
          placeholder="Search by name, ID or mobile"
          className="min-w-0"
        />
        <div className="inline-flex overflow-hidden rounded-md border">
          <Button
            type="button"
            size="sm"
            variant={isActive ? 'default' : 'ghost'}
            className="rounded-none"
            onClick={() => setIsActive(true)}
          >
            Active
          </Button>
          <Button
            type="button"
            size="sm"
            variant={isActive ? 'ghost' : 'default'}
            className="rounded-none"
            onClick={() => setIsActive(false)}
          >
            Inactive
          </Button>
        </div>
        <div className="flex items-center gap-2 min-w-0 w-full justify-center md:w-auto md:justify-start">
          <DatePickerInput
            value={dateFrom}
            onChange={setDateFrom}
            maxDate={today}
            className="min-w-0"
          />
          <span className="text-sm text-muted-foreground shrink-0">To</span>
          <DatePickerInput
            value={dateTo}
            onChange={setDateTo}
            maxDate={today}
            className="min-w-0"
          />
        </div>
        <ExportButton onExport={() => onExport({ dateFrom, dateTo, isActive })} />
      </PageToolbar>
      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load attendance"
          onRetry={() => void refetch()}
          fill
          className="order-4 lg:order-0"
        />
      ) : (
        <>
          <div className="order-2 lg:order-0">
            <StatCardGrid stats={summary} isLoading={isLoading} />
          </div>
          <DataTable
            data={filtered}
            columns={columns}
            isLoading={isLoading}
            stickyColumnId="amName"
            className="order-4 lg:order-0 lg:flex-1"
          />
        </>
      )}
    </div>
  );
}
